/* Poser un prix, demander une baisse, trancher.
 *
 * Aucune de ces écritures ne touche une table : tout passe par les fonctions
 * de la base. Une politique RLS contrôle la ligne produite, pas les colonnes
 * touchées — un agent autorisé à monter un tarif client pourrait le baisser
 * dans le même mouvement. Les fonctions, elles, savent ce qu'elles écrivent,
 * et ferment la période précédente avant d'en ouvrir une nouvelle.
 */

import { supabase } from "@/lib/supabase";

function echoue(erreur: { message: string } | null): void {
  if (!erreur) return;
  /* Les messages des fonctions sont écrits pour être lus : on les rend tels
     quels plutôt que de les remplacer par une phrase vague. */
  throw new Error(erreur.message.replace(/^.*?:\s*/, ""));
}

export async function poserPrixUsine(
  produitId: string, prix: number, motif?: string,
): Promise<void> {
  const { error } = await supabase.rpc("poser_prix_usine", {
    p_produit: produitId, p_prix: prix, p_motif: motif ?? null,
  });
  echoue(error);
}

export async function poserPlancherAgent(
  agentId: string, produitId: string, prix: number, motif?: string,
): Promise<void> {
  const { error } = await supabase.rpc("poser_prix_agent", {
    p_agent: agentId, p_produit: produitId, p_prix: prix, p_motif: motif ?? null,
  });
  echoue(error);
}

export async function poserTarifClient(
  clientId: string, produitId: string, prix: number, motif?: string,
): Promise<void> {
  const { error } = await supabase.rpc("poser_prix_client", {
    p_client: clientId, p_produit: produitId, p_prix: prix, p_motif: motif ?? null,
  });
  echoue(error);
}

/** Pose en nombre, pour l'import : un aller-retour au lieu de plusieurs centaines. */
export async function poserPrixUsineLot(
  lignes: readonly { produit_id: string; prix: number }[],
): Promise<number> {
  if (!lignes.length) return 0;
  const { data, error } = await supabase.rpc("poser_prix_usine_lot", { p_lignes: lignes });
  echoue(error);
  return Number(data ?? 0);
}

export async function poserPrixAgentLot(
  lignes: readonly { agent_id: string; produit_id: string; prix_mini: number }[],
): Promise<number> {
  if (!lignes.length) return 0;
  const { data, error } = await supabase.rpc("poser_prix_agent_lot", { p_lignes: lignes });
  echoue(error);
  return Number(data ?? 0);
}

/** Demander à descendre sous son plancher. Une seule demande en attente par référence. */
export async function demanderBaisse(d: {
  produitId: string;
  clientId: string | null;
  venteId: string | null;
  prix: number;
  plancher: number;
  motif?: string;
}): Promise<void> {
  const { error } = await supabase.rpc("demander_baisse", {
    p_produit: d.produitId, p_client: d.clientId, p_vente: d.venteId,
    p_prix: d.prix, p_plancher: d.plancher, p_motif: d.motif ?? null,
  });
  echoue(error);
}

/**
 * Accepter, c'est abaisser le plancher de CET agent sur CETTE référence —
 * pas le prix de référence du produit, et pas le plancher des autres. La
 * trace reste dans l'historique du plancher, la demande pour motif.
 */
export async function trancherDemande(
  demandeId: string, accepte: boolean, reponse?: string,
): Promise<void> {
  const { error } = await supabase.rpc("trancher_demande", {
    p_demande: demandeId, p_accepte: accepte, p_reponse: reponse ?? null,
  });
  echoue(error);
}

/** Répercuter sur le tarif du client ce qu'un devis a vendu plus cher. */
export async function propagerHausses(
  clientId: string,
  hausses: readonly { produitId: string; apres: number }[],
  reference: string,
): Promise<number> {
  let n = 0;
  for (const h of hausses) {
    try {
      await poserTarifClient(clientId, h.produitId, h.apres, `devis ${reference}`);
      n += 1;
    } catch {
      /* Un tarif qui refuse de monter ne doit pas faire échouer le devis :
         le devis est le fait, le tarif n'en est que la conséquence. */
    }
  }
  return n;
}
